import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Eye, EyeOff } from "lucide-react";
import { trackRegistration } from "@/utils/gtag";

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: ""
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setLoading(true);
    
    try {
      await register(formData.name, formData.email, formData.password, formData.phone);
      // Track registration conversion for Google Ads
      trackRegistration();
      toast.success("Account created successfully!");
      navigate("/dashboard");
    } catch (error) {
      const message = error.response?.data?.detail || "Registration failed";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-off-white flex" data-testid="register-page">
      {/* Left Side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_premium-pack-3/artifacts/571w2mf7_PXL_20260327_224713378.PORTRAIT.jpg')`
          }}
        >
          <div className="absolute inset-0 bg-charcoal/30" />
        </div>
        <div className="absolute top-12 left-12 right-12">
          <Link to="/" className="flex flex-col">
            <span className="font-heading text-3xl italic text-white">The Pack Standard</span>
            <span className="text-olive text-sm tracking-wider">PUPPY ACADEMY</span>
          </Link>
        </div>
        <div className="absolute bottom-12 left-12 right-12">
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-8">
            <h3 className="font-heading text-2xl text-charcoal mb-4">Join Our Academy</h3>
            <ul className="space-y-3 text-stone">
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-olive rounded-full" />
                Structured 4-week programs
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-olive rounded-full" />
                Weekly progress reports
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-olive rounded-full" />
                Home pickup and drop-off
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-olive rounded-full" />
                Graduation certificate
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 md:px-16 lg:px-24 py-12">
        <Link
          to="/"
          className="inline-flex items-center text-stone hover:text-olive transition-colors mb-8"
          data-testid="back-link"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to home
        </Link>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="font-heading text-4xl md:text-5xl text-charcoal mb-4">
            Create Account
          </h1>
          <p className="text-stone text-lg mb-8">
            Join The Pack Standard Academy and give your puppy the best start.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-charcoal font-medium">
                Full Name
              </Label>
              <Input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                placeholder="John Smith"
                className="input-premium"
                required
                data-testid="name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-charcoal font-medium">
                Email Address
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
                className="input-premium"
                required
                data-testid="email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-charcoal font-medium">
                Phone Number
              </Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="0400 000 000"
                className="input-premium"
                required
                data-testid="phone-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-charcoal font-medium">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Create a password"
                  className="input-premium pr-10"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone hover:text-charcoal"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-charcoal font-medium">
                Confirm Password
              </Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm your password"
                className="input-premium"
                required
                data-testid="confirm-password-input"
              />
            </div>

            <Button
              type="submit"
              className="btn-primary w-full"
              disabled={loading}
              data-testid="register-submit"
            >
              {loading ? "Creating Account..." : "Create Account"}
            </Button>
          </form>

          <p className="mt-8 text-center text-stone">
            Already have an account?{" "}
            <Link
              to="/login"
              className="text-olive hover:text-olive-dark font-medium transition-colors"
              data-testid="login-link"
            >
              Sign in
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default RegisterPage;
